import { useCallback, useRef, useState } from 'react'
import {
  fetchEventSource,
  EventStreamContentType,
} from '@microsoft/fetch-event-source'
import { message } from 'antd'
import { getRecoil, setRecoil } from 'recoil-nexus'
import { getAuthToken, hasJwtNotExpired } from '@/lib/auth/jwt'
import {
  gemNotEnoughModalOpenState,
  newUserGemNotEnoughModalOpenState,
} from '@/store/chat'
import { userState } from '@/store/user'

export type RawSSEEvent = Record<string, any> & {
  event?: string
}

export type UseSSEMessageStreamOptions = {
  url: string
  method?: 'GET' | 'POST'
  headers?: Record<string, string>
  body?: any
  getContent?: (raw: RawSSEEvent) => string | undefined
  getFinalContent?: (raw: RawSSEEvent) => string | undefined
  getStatusCode?: (raw: RawSSEEvent) => number | undefined
  onMessageChunk?: (chunk: string, raw: RawSSEEvent) => void
  onMessageEnd?: (finalContent: string, raw: RawSSEEvent) => void
  onBusinessError?: (statusCode: number | undefined, raw: RawSSEEvent) => void
  onNetworkError?: () => void
  onEvent?: (event: string, raw: RawSSEEvent) => void
  /**
   * Auto open gem-not-enough modal when status_code is 402.
   * Default true.
   */
  autoHandleGemNotEnough?: boolean
  /**
   * Auto show error toast when business error returns content.
   * Default true.
   */
  autoShowErrorToast?: boolean
  /**
   * Whether to enable internal state updates (streamingContent/isStreaming etc).
   * Default true.
   */
  enableInternalState?: boolean
}

export type SSEMessageStreamStartOptions = Partial<
  Pick<UseSSEMessageStreamOptions, 'url' | 'method' | 'headers' | 'body'>
>

const stringifyBody = (body: any) => {
  if (body == null) return undefined
  if (typeof body === 'string') return body
  return JSON.stringify(body)
}

const isNewUserForGemModal = (userId: string | number | undefined) => {
  if (userId == null) return false
  const numericUserId = Number(userId)
  if (Number.isNaN(numericUserId)) return false
  return numericUserId / 1000000000 + 3 * 86400 > Date.now() / 1000
}

export const useSSEMessageStream = (options: UseSSEMessageStreamOptions) => {
  const optionsRef = useRef(options)
  optionsRef.current = options

  const controllerRef = useRef<AbortController | null>(null)
  const [isActive, setIsActive] = useState(false)
  const [streamingContent, setStreamingContent] = useState<string>('')
  const [finalContent, setFinalContent] = useState<string | null>(null)
  const [isWaitingForFirstChunk, setIsWaitingForFirstChunk] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)

  const accumulatedContentRef = useRef('')
  const hasStartedRef = useRef(false)

  const stop = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort()
      controllerRef.current = null
    }
    setIsActive(false)
  }, [])

  const reset = useCallback(() => {
    accumulatedContentRef.current = ''
    hasStartedRef.current = false
    setStreamingContent('')
    setFinalContent(null)
    setIsWaitingForFirstChunk(false)
    setIsStreaming(false)
  }, [])

  const buildHeaders = useCallback(async () => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Platform: 'web',
    }
    const token = getAuthToken()
    if (token && hasJwtNotExpired(token)) {
      headers.token = token
    }
    return headers
  }, [])

  const start = useCallback(
    async (override?: SSEMessageStreamStartOptions) => {
      const opts = optionsRef.current
      const url = override?.url ?? opts.url
      if (!url) return

      stop()
      reset()
      const shouldUpdateState = opts.enableInternalState !== false
      if (shouldUpdateState) {
        setIsWaitingForFirstChunk(true)
      }

      setIsActive(true)
      const controller = new AbortController()
      controllerRef.current = controller

      try {
        const baseHeaders = await buildHeaders()
        const headers = override?.headers
          ? { ...(baseHeaders ?? {}), ...override.headers }
          : { ...(baseHeaders ?? {}), ...(opts.headers ?? {}) }

        await fetchEventSource(url, {
          method: override?.method ?? opts.method ?? 'POST',
          credentials: 'include',
          headers,
          body: stringifyBody(override?.body ?? opts.body),
          signal: controller.signal,
          onopen: async (response) => {
            if (
              response.ok &&
              response.headers.get('content-type') === EventStreamContentType
            ) {
              return // everything's good
            } else if (
              response.status >= 400 &&
              response.status < 500 &&
              response.status !== 429
            ) {
              // client-side errors are usually fatal
              throw new Error(`Failed to start stream: ${response.status}`)
            }
          },
          onmessage: (event) => {
            if (!event.data) return
            try {
              const raw = JSON.parse(event.data) as RawSSEEvent
              const eventName = typeof raw.event === 'string' ? raw.event : ''
              if (!eventName) return

              if (eventName === 'message') {
                const getContent =
                  opts.getContent ??
                  ((data) => data.content as string | undefined)
                const chunk = getContent(raw)
                if (!chunk) return
                if (!hasStartedRef.current) {
                  hasStartedRef.current = true
                  if (shouldUpdateState) {
                    setIsWaitingForFirstChunk(false)
                    setIsStreaming(true)
                  }
                }
                accumulatedContentRef.current += chunk
                if (shouldUpdateState) {
                  setStreamingContent(accumulatedContentRef.current)
                }
                opts.onMessageChunk?.(chunk, raw)
                return
              }

              if (eventName === 'message_end') {
                const getFinal =
                  opts.getFinalContent ??
                  ((data) => data.full_content as string | undefined)
                const finalText = getFinal(raw) ?? accumulatedContentRef.current
                if (shouldUpdateState) {
                  setFinalContent(finalText)
                  setStreamingContent('')
                  setIsStreaming(false)
                  setIsWaitingForFirstChunk(false)
                }
                opts.onMessageEnd?.(finalText, raw)
                stop() // Close explicitly
                return
              }

              if (eventName === 'error') {
                const getStatus =
                  opts.getStatusCode ??
                  ((data) => data.status_code as number | undefined)
                const statusCode = getStatus(raw)
                const errorContent =
                  typeof raw.content === 'string' ? raw.content : ''

                if (
                  errorContent &&
                  opts.autoShowErrorToast !== false &&
                  statusCode !== 402
                ) {
                  message.error(errorContent)
                }

                if (
                  statusCode === 402 &&
                  opts.autoHandleGemNotEnough !== false
                ) {
                  const user = getRecoil(userState)
                  const isNewUser = isNewUserForGemModal(user?.user_id)
                  setRecoil(
                    isNewUser
                      ? newUserGemNotEnoughModalOpenState
                      : gemNotEnoughModalOpenState,
                    true
                  )
                }

                if (shouldUpdateState) {
                  setIsStreaming(false)
                  setIsWaitingForFirstChunk(false)
                }
                opts.onBusinessError?.(statusCode, raw)
                stop() // Close explicitly
                return
              }

              opts.onEvent?.(eventName, raw)
            } catch (error) {
              console.error('Failed to parse SSE message:', error)
            }
          },
          onerror: (err) => {
            if (shouldUpdateState) {
              setIsStreaming(false)
              setIsWaitingForFirstChunk(false)
            }
            opts.onNetworkError?.()
            throw err // Rethrow to stop
          },
        })
      } catch (err) {
        if (controller.signal.aborted) {
          // Ignore abort errors
          return
        }
        console.error('SSE connection failed', err)
        if (shouldUpdateState) {
          setIsWaitingForFirstChunk(false)
          setIsStreaming(false)
        }
        opts.onNetworkError?.()
      }
    },
    [buildHeaders, reset, stop]
  )

  return {
    start,
    stop,
    reset,
    streamingContent,
    finalContent,
    isWaitingForFirstChunk,
    isStreaming,
    isActive,
  }
}

export default useSSEMessageStream
